import { updateSsObject, getSsObj, clearSs, setSs, getSs } from "./storage"
import { registerDrag } from "../utils/draggable"
import { throttle } from "../utils/helpers"
import { Emitter } from "./event-emitter"

export const VIDEO_STORAGE_PREFIX = "youtube_video_"
export const CONSTANT_CLASS = "video-player"

export const getYouTubeThumbnail = (videoId) =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`

export function getYouTubeVideoId(url) {
  const match =
    /(?:youtu\.be\/|youtube\.com\/(?:shorts\/|embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/.exec(
      url,
    )
  return match ? match[1] : null
}

export function saveCurrentTime(videoPlayerEl, videoId, storageKey) {
  if (videoPlayerEl.currentTime) {
    const currentTime = videoPlayerEl.currentTime.toFixed(2).toString()
    updateSsObject(storageKey, { currentTime })
    /* eslint no-console: "off" */
    console.log(`Saved time for ${videoId}: ${currentTime}s`)
  }
}

export function timeStringToSeconds(timeStr) {
  if (!timeStr || typeof timeStr !== "string") {
    return
  }
  const [minutes, seconds] = timeStr.split(":").map(Number)
  return minutes * 60 + seconds
}

export function loadSavedTime(storageKey) {
  return parseFloat(getSsObj(storageKey)?.currentTime || 0)
}

export function handleOnLoadMetadata(videoPlayerEl, storageKey) {
  const savedTime = loadSavedTime(storageKey)
  if (savedTime) {
    /* eslint-disable no-console */
    console.log(
      `Found saved time for this video: ${savedTime.toFixed(
        2,
      )}s. Setting current time...`,
    )
    videoPlayerEl.currentTime = savedTime
  }
}

// Track currenty playing video element so we can pause it when a new one starts

/**
 * Returns a throttled function that saves the current playback time of a video player element,
 * only once every second,
 * pauses any other playing video, and updates the reference to the currently playing video.
 *
 * @param {HTMLVideoElement} videoPlayerEl - The video player element whose time should be saved.
 * @param {string} videoId - The unique identifier for the video.
 * @param {string} storageKey - The key used to store the current time in storage.
 * @returns {Function} A throttled function to handle time updates.
 */
export const throttledTimeUpdate = (videoPlayerEl, videoId, storageKey) =>
  throttle(() => {
    saveCurrentTime(videoPlayerEl, videoId, storageKey)

    const prevVideoId = getSs("current-video-playing")
    let prevVideoEl

    if (!prevVideoId) {
      setSs("current-video-playing", videoId)
    } else if (prevVideoId && prevVideoId !== videoId) {
      setSs("current-video-playing", videoId)
      // First check if a pip player is present
      prevVideoEl = document.querySelector(
        `[data-video-id="${prevVideoId}"][data-is-pip="true"]`,
      )

      if (!prevVideoEl) {
        // If not, find the component video player
        prevVideoEl = document.querySelector(`[data-video-id="${prevVideoId}"]`)
      }

      if (prevVideoEl) {
        const prevPlayer = prevVideoEl.querySelector(".video-player__player")
        if (prevPlayer && prevPlayer.pause) prevPlayer.pause()
      }
    }
  }, 1000)

export function positionPipPlayer(videoWrapperEl, storageKey) {
  const isInPipMode = getSsObj(storageKey)?.isInPipMode || false

  if (isInPipMode) {
    videoWrapperEl.dataset.pip = "true"
  }
  const position = getSsObj("video-player-position")

  if (isInPipMode && position) {
    videoWrapperEl.style.transform = `translate(${position.x}px, ${position.y}px)`
    videoWrapperEl.setAttribute("data-x", position.x)
    videoWrapperEl.setAttribute("data-y", position.y)
  } else {
    videoWrapperEl.style.transform = "translate(0px, 0px)"
    videoWrapperEl.setAttribute("data-x", "0")
    videoWrapperEl.setAttribute("data-y", "0")
  }
}

/**
 * Cleans up and removes the Picture-in-Picture player from the DOM.
 */
export function destroyPipPlayer(currentPipPlayerElement) {
  if (currentPipPlayerElement) {
    clearSs("pip-video")
    const youtubeVideo = currentPipPlayerElement.querySelector("youtube-video")
    if (youtubeVideo) {
      try {
        // Try common methods or specific ones if you know your component's API
        if (typeof youtubeVideo.pause === "function") {
          youtubeVideo.pause()
        } else {
          console.warn(
            "Could not find a 'pause' method on youtube-video component. Video might continue playing.",
          )
        }
      } catch (error) {
        console.error("Error attempting to pause youtube-video:", error)
      }
    }
    currentPipPlayerElement.remove()

    Emitter.emit("close-pip-video")

    console.log("PiP player destroyed.")
  } else {
    console.log("No PiP player to destroy.")
  }
}

/**
 * Creates and appends the Picture-in-Picture (PiP) video player
 * to the document body.
 *
 */
export function createPipPlayer(
  videoId,
  storageKey,
  togglePipMode,
  isCreatedFromPip = false,
) {
  const videoPlayerWrapper = document.createElement("div")
  videoPlayerWrapper.className = `${CONSTANT_CLASS} draggable ${CONSTANT_CLASS}--pip-window`
  videoPlayerWrapper.setAttribute("data-is-pip", "true")
  videoPlayerWrapper.setAttribute("data-video-id", videoId)
  positionPipPlayer(videoPlayerWrapper, storageKey)

  const dragHandle = document.createElement("div")
  dragHandle.className = `${CONSTANT_CLASS}__drag-handle`
  dragHandle.innerHTML = `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      fill="#ffffff"
      version="1.1"
      id="icon"
      width="24px"
      height="24px"
      viewBox="0 0 32 32"
      xml:space="preserve"
    >
      <style type="text/css">
        .st0 {
          fill: none;
        }
      </style>
      <title>draggable</title>
      <rect x="10" y="6" width="4" height="4" />
      <rect x="18" y="6" width="4" height="4" />
      <rect x="10" y="14" width="4" height="4" />
      <rect x="18" y="14" width="4" height="4" />
      <rect x="10" y="22" width="4" height="4" />
      <rect x="18" y="22" width="4" height="4" />
      <rect
        id="_Transparent_Rectangle_"
        class="st0"
        width="32"
        height="32"
      />
    </svg>
  `
  registerDrag(dragHandle)
  videoPlayerWrapper.appendChild(dragHandle)

  const exitPipBtn = document.createElement("div")
  exitPipBtn.className = `${CONSTANT_CLASS}__exit-pip-btn`
  exitPipBtn.innerHTML = `
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.625 17.1428L17.625 2.85711"
        stroke="#F6F6F7"
        stroke-width="2.5"
        stroke-linecap="square"
      />
      <path
        d="M2.9375 2.5L17.625 17.1429"
        stroke="#F6F6F7"
        stroke-width="2.5"
        stroke-linecap="square"
      />
    </svg>
  `

  exitPipBtn.addEventListener("click", () => {
    if (togglePipMode) {
      togglePipMode(videoId, null, storageKey)
      destroyPipPlayer(videoPlayerWrapper)
    } else if (isCreatedFromPip) {
      // If this was created from a persistent PIP, just destroy it
      destroyPipPlayer(videoPlayerWrapper)
    }
  })

  videoPlayerWrapper.appendChild(exitPipBtn)

  const mediaController = document.createElement("media-controller")

  const youtubeVideo = document.createElement("youtube-video")
  youtubeVideo.className = `${CONSTANT_CLASS}__player`
  youtubeVideo.setAttribute("slot", "media")
  youtubeVideo.setAttribute("crossorigin", "")
  youtubeVideo.setAttribute("height", "390")
  youtubeVideo.setAttribute("width", "640")
  youtubeVideo.setAttribute("rel", "0") // YouTube parameter
  youtubeVideo.setAttribute("allow", "picture-in-picture")
  youtubeVideo.setAttribute("src", `https://www.youtube.com/embed/${videoId}`) // Use embed URL format

  // Mobile-specific attributes to ensure iframe controls appear consistently
  youtubeVideo.setAttribute("playsinline", "")
  youtubeVideo.setAttribute("controls", "")
  youtubeVideo.setAttribute("preload", "metadata")

  // Set current time after a short delay to ensure video is loaded
  setTimeout(() => {
    youtubeVideo.currentTime = loadSavedTime(storageKey) || 0
  }, 100)

  const handleThrottledTimeUpdate = throttledTimeUpdate(
    youtubeVideo,
    videoId,
    storageKey,
  )

  // Add all necessary event listeners for proper functionality
  youtubeVideo.addEventListener("loadedmetadata", handleThrottledTimeUpdate)
  youtubeVideo.addEventListener("timeupdate", handleThrottledTimeUpdate)
  youtubeVideo.addEventListener("pause", () => {
    saveCurrentTime(youtubeVideo, videoId, storageKey)
  })
  youtubeVideo.addEventListener("ended", () => {
    saveCurrentTime(youtubeVideo, videoId, storageKey)
  })

  mediaController.appendChild(youtubeVideo)

  const videoControls = document.createElement("div")
  videoControls.className = `${CONSTANT_CLASS}__controls`
  videoControls.innerHTML = `
    <div style="padding: 10px">
      <media-control-bar>
        <media-time-range></media-time-range>
      </media-control-bar>
      <media-control-bar class="second">
        <media-play-button></media-play-button>
        <media-seek-backward-button></media-seek-backward-button>
        <media-seek-forward-button></media-seek-forward-button>
        <div class="${CONSTANT_CLASS}__volume-controls">
          <media-mute-button></media-mute-button>
          <div class="${CONSTANT_CLASS}__volume-range">
            <media-volume-range></media-volume-range>
          </div>
        </div>
        <media-time-display showduration></media-time-display>
        <div class="${CONSTANT_CLASS}__util-controls">
          <div class="${CONSTANT_CLASS}__pip-button">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 6.9375C3 6.48995 3.17779 6.06072 3.49426 5.74426C3.81072 5.42779 4.23995 5.25 4.6875 5.25H19.3125C19.7601 5.25 20.1893 5.42779 20.5057 5.74426C20.8222 6.06072 21 6.48995 21 6.9375V17.0625C21 17.5101 20.8222 17.9393 20.5057 18.2557C20.1893 18.5722 19.7601 18.75 19.3125 18.75H4.6875C4.23995 18.75 3.81072 18.5722 3.49426 18.2557C3.17779 17.9393 3 17.5101 3 17.0625V6.9375ZM4.6875 6.375C4.53832 6.375 4.39524 6.43426 4.28975 6.53975C4.18426 6.64524 4.125 6.78832 4.125 6.9375V17.0625C4.125 17.2117 4.18426 17.3548 4.28975 17.4602C4.39524 17.5657 4.53832 17.625 4.6875 17.625H19.3125C19.4617 17.625 19.6048 17.5657 19.7102 17.4602C19.8157 17.3548 19.875 17.2117 19.875 17.0625V6.9375C19.875 6.78832 19.8157 6.64524 19.7102 6.53975C19.6048 6.43426 19.4617 6.375 19.3125 6.375H4.6875Z"
                fill="#DBDADB"
              />
              <path
                d="M12 12.5625C12 12.4133 12.0593 12.2702 12.1648 12.1648C12.2702 12.0593 12.4133 12 12.5625 12H18.1875C18.3367 12 18.4798 12.0593 18.5852 12.1648C18.6907 12.2702 18.75 12.4133 18.75 12.5625V15.9375C18.75 16.0867 18.6907 16.2298 18.5852 16.3352C18.4798 16.4407 18.3367 16.5 18.1875 16.5H12.5625C12.4133 16.5 12.2702 16.4407 12.1648 16.3352C12.0593 16.2298 12 16.0867 12 15.9375V12.5625Z"
                fill="#DBDADB"
              />
            </svg>
          </div>
          <media-fullscreen-button></media-fullscreen-button>
        </div>
      </media-control-bar>
    </div>
  `
  mediaController.appendChild(videoControls)
  videoPlayerWrapper.appendChild(mediaController)

  document.body.appendChild(videoPlayerWrapper)

  // Initialize the player properly after DOM insertion
  setTimeout(() => {
    // Ensure the video is properly initialized
    if (youtubeVideo.readyState === 0) {
      youtubeVideo.addEventListener(
        "loadedmetadata",
        () => {
          youtubeVideo.currentTime = loadSavedTime(storageKey) || 0
          // Force media controls to sync with actual video state
          setTimeout(() => {
            if (youtubeVideo.paused) {
              youtubeVideo.dispatchEvent(new Event("pause"))
            }
          }, 50)
        },
        { once: true },
      )
    } else {
      // Video is already loaded, set current time immediately
      youtubeVideo.currentTime = loadSavedTime(storageKey) || 0
      // Force media controls to sync with actual video state
      setTimeout(() => {
        if (youtubeVideo.paused) {
          youtubeVideo.dispatchEvent(new Event("pause"))
        }
      }, 50)
    }

    // If this was created from PiP storage, ensure proper state initialization
    if (isCreatedFromPip) {
      console.log(
        "Restored PiP video at saved time position, ready for manual playback",
      )

      // Additional mobile-specific fixes for iframe controls
      setTimeout(() => {
        // Force a pause state to ensure controls show correctly on mobile
        if (youtubeVideo.paused) {
          youtubeVideo.dispatchEvent(new Event("pause"))
          youtubeVideo.dispatchEvent(new Event("loadstart"))
        }

        // Ensure media-chrome controls are properly connected
        const mediaController = youtubeVideo.closest("media-controller")
        if (mediaController && mediaController.associateElement) {
          mediaController.associateElement(youtubeVideo)
        }
      }, 200)
    }
  }, 100)

  return videoPlayerWrapper
}

let pipElement

export function togglePipMode(videoId, videoSrc, storageKey) {
  const videoWrapperEl = document.querySelector(`[data-video-id="${videoId}"]`)
  if (!videoWrapperEl) {
    console.warn("Video wrapper element not found.")
    return
  }
  const videoPlayerEl = videoWrapperEl.querySelector(
    `.${CONSTANT_CLASS}__player`,
  )
  if (!videoPlayerEl) {
    console.warn("Video player element not found within the wrapper.")
    return
  }

  const isActive = videoWrapperEl.dataset.pip === "true"

  if (isActive) {
    videoPlayerEl.currentTime = loadSavedTime(storageKey) || 0
    clearSs("video-player-position")
    clearSs("pip-video")

    updateSsObject(storageKey, { isInPipMode: false })
    if (!pipElement) {
      pipElement = document.querySelector(
        `.${CONSTANT_CLASS}--pip-window[data-pip="true"]`,
      )
    }
    destroyPipPlayer(pipElement)
    pipElement = null

    videoWrapperEl.dataset.pip = "false"
    videoWrapperEl.style.display = "block"
  } else {
    const pipObject = getSsObj("pip-video")
    if (pipObject) {
      return console.warn(
        "A video is already in PIP mode. Please exit PIP mode before starting a new one.",
      )
    }
    videoWrapperEl.dataset.pip = "true"
    videoWrapperEl.style.display = "none"

    // Pause the video before switching to PiP
    if (videoPlayerEl.pause && typeof videoPlayerEl.pause === "function") {
      videoPlayerEl.pause()
    }

    updateSsObject(storageKey, { isInPipMode: true })
    updateSsObject("pip-video", {
      videoId,
      videoSrc,
    })

    pipElement = createPipPlayer(videoId, storageKey, togglePipMode)
  }
}
